import PropTypes from 'prop-types';
import Button from 'Components/Atoms/Button/Button';
import { useTranslation } from 'react-i18next';
import { useLayoutEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getContact } from 'Services/invoices';
import Loading from 'Components/Atoms/Loading/Loading';
import { trackError } from 'Utils/errorMonitoring';
import ContactMergeDrawerItem from './ContactMergeDrawerItem';

function useContacts(ids) {
    return useQuery({
        queryKey: ['contacts', ids],
        queryFn: () => Promise.all(ids.map(getContact)),
        onError: (error) => {
            trackError(error);
        },
    });
}

export default function ContactMergeDrawer(props) {
    const { onSubmit, onError, onCancel, contact_duplicated_ids, submitButtonText, cancelButtonText, onClose } = props;
    const { data = [], isLoading, error } = useContacts(contact_duplicated_ids);

    const sortedContacts = data.sort((a, b) => {
        if (a.integration_id && !b.integration_id) {
            return -1;
        }
        if (!a.integration_id && b.integration_id) {
            return 1;
        }
        return 0;
    });

    const someHasIntegration = sortedContacts.some((contact) => contact.integration_id);
    const [processing, setProcessing] = useState(false);
    const { t } = useTranslation('contacts');
    const [contactSelectedId, setContactSelectedId] = useState(contact_duplicated_ids[0]);

    const defaultSubmitButtonText = t('save');
    const defaultCancelButtonText = t('cancel');
    const submitButton = submitButtonText || defaultSubmitButtonText;
    const cancelButton = cancelButtonText || defaultCancelButtonText;

    const handleSubmit = async () => {
        setProcessing(true);
        const otherContacts = contact_duplicated_ids.filter((id) => id !== contactSelectedId);
        try {
            await onSubmit({ contactId: contactSelectedId, contactIdsToMerge: otherContacts });
        } catch (error) {
            onError(error);
        } finally {
            setProcessing(false);
        }
    };

    useLayoutEffect(() => {
        if (sortedContacts.length > 0) {
            setContactSelectedId(sortedContacts[0].id);
        }
    }, [isLoading]);

    if (error) {
        return (
            <div className="text-center py-12">
                <div className="text-gray-500 text-pretty px-2 mb-6">{t('merge-contacts-load-error')}</div>
                <Button variant="primary" onClick={onClose}>
                    {t('accept')}
                </Button>
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full">
            <div className="text-gray-500 mb-6">{t('merge-contacts-description')}</div>

            {isLoading && (
                <div className="flex justify-center items-center py-6">
                    <Loading />
                </div>
            )}

            {!isLoading && (
                <>
                    <div className="grid gap-4 mb-6">
                        {data.map((contact) => (
                            <ContactMergeDrawerItem
                                key={contact.id}
                                {...contact}
                                checked={contact.id === contactSelectedId}
                                onChange={() => setContactSelectedId(contact.id)}
                                disabled={someHasIntegration && !contact.integration_id}
                            />
                        ))}
                    </div>

                    <div className="grow" />

                    <div className="sticky bottom-0 bg-white pt-2 pb-4 z-10">
                        <div className="flex gap-2">
                            <Button variant="primary" isBusy={processing} onClick={handleSubmit} className="grow">
                                {submitButton}
                            </Button>
                            <Button className="grow" onClick={onCancel}>
                                {cancelButton}
                            </Button>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}

ContactMergeDrawer.propTypes = {
    onClose: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    onCancel: PropTypes.func.isRequired,
    onError: PropTypes.func.isRequired,
    submitButtonText: PropTypes.string,
    cancelButtonText: PropTypes.string,
    contact_duplicated_ids: PropTypes.arrayOf(PropTypes.string).isRequired,
};
